import apiFetch from '@scale/frontend-lib/api-fetch';
import _ from 'lodash';

import { z } from 'zod';

import { validateStepContentMatches } from './helpers';

export const processChunkingStrategies = [
  'ChunkBySentence',
  'ChunkByNewline',
  'ChunkByStepNumber',
  'ChunkByRegex',
  'ChunkWithGPT',
  'ChunkByDelimiterPair',
] as const;
export type ProcessChunkingStrategy = (typeof processChunkingStrategies)[number];

export const ProcessChunkingStrategyNameByStrategy = {
  ChunkBySentence: 'Sentence',
  ChunkByNewline: 'Newline',
  ChunkByStepNumber: 'Step Number',
  ChunkByRegex: 'Regex',
  ChunkWithGPT: 'GPT',
};

export type ProcessChunkingStrategyParams = z.infer<
  (typeof ProcessChunkingStrategyParamsMap)[keyof typeof ProcessChunkingStrategyMap]
>;

export interface ProcessChunkingStrategyWithParams {
  strategy: ProcessChunkingStrategy;
  params?: ProcessChunkingStrategyParams;
}

export const ProcessChunkingStrategyParamsMap: {
  [key in ProcessChunkingStrategy]: z.ZodObject<any>;
} = {
  ChunkBySentence: z
    .object({})
    .describe(
      'Splits the text into chunks based on sentence endings (periods, exclamation marks, or question marks followed by a space). No additional parameters required.',
    ),
  ChunkByNewline: z
    .object({})
    .describe(
      'Divides the text into chunks at each newline character. Useful for pre-formatted text with logical breaks. No additional parameters required.',
    ),
  ChunkByStepNumber: z
    .object({})
    .describe(
      "Separates the text into chunks based on step numbers (e.g., '**Step 1:', '**Step 2:'). Ideal for numbered instructions or processes. No additional parameters required.",
    ),
  ChunkByRegex: z
    .object({
      regex: z
        .string()
        .describe('The regular expression pattern used to split the text into chunks.'),
      inclusive: z
        .boolean()
        .describe(
          'If true, the regex pattern will be included in the chunks. If false, the regex pattern will be excluded from the chunks. Warning: this expects the regex pattern to be at the front of each chunk!',
        )
        .optional(),
    })
    .describe(
      "Splits the text using a custom regular expression. The 'regex' parameter allows you to define your own splitting pattern for flexible chunking.",
    ),
  ChunkWithGPT: z
    .object({
      systemPrompt: z
        .string()
        .describe('Custom instructions to guide the AI in chunking the text.')
        .optional(),
      modelStrategyName: z
        .string()
        .describe(
          'The name of the model strategy to use, similar to Response Selector configuration.',
        )
        .optional(),
      model: z
        .string()
        .describe(
          'The specific model ID to use for chunking, similar to the Response Selector model param.',
        )
        .optional(),
      temperature: z
        .number()
        .describe(
          "Controls the randomness of the AI's output. Lower values make the output more deterministic.",
        )
        .optional(),
      model_parameters: z
        .any()
        .describe(
          'Additional parameters to pass to the model, similar to the Response Selector model_parameters.',
        )
        .optional(),
      skipValidation: z
        .boolean()
        .describe(
          'If true, skips the validation step after chunking which ensures that the chunked content matches the original PERFECTLY.',
        )
        .optional(),
      maxLevenshteinDistance: z
        .number()
        .describe(
          'If validation is enabled, the maximum allowed Levenshtein distance between the original text and the reconstructed text from chunks. Default is 0 (perfect match).',
        )
        .optional(),
    })
    .describe(
      "Uses GPT to intelligently divide the text into logical chunks. Optional parameters include 'systemPrompt' to guide the AI, 'modelStrategyName' to specify the strategy in the same way as we configure Response Selectors, 'model' to choose a specific model, and 'temperature' to control randomness in the output.",
    ),
  ChunkByDelimiterPair: z.object({
    startDelimiter: z.string().describe('The start delimiter to split the text into chunks.'),
    endDelimiter: z.string().describe('The end delimiter to split the text into chunks.'),
  }),
};
export const ProcessChunkingStrategyMap: {
  [key in ProcessChunkingStrategy]: (
    text: string,
    params?: any,
  ) => Promise<{
    header?: string;
    footer?: string;
    chunks: string[];
  }>;
} = {
  ChunkBySentence: async (
    text: string,
  ): Promise<{ header?: string; footer?: string; chunks: string[] }> => {
    const sentenceEndingsRegex = /(?<=[.!?])\s+/;
    const chunks = text.split(sentenceEndingsRegex);
    return Promise.resolve({ chunks });
  },
  ChunkByNewline: async (
    text: string,
  ): Promise<{ header?: string; footer?: string; chunks: string[] }> => {
    const chunks = text.split(/\r?\n/);
    return Promise.resolve({ chunks });
  },
  ChunkByStepNumber: async (
    text: string,
  ): Promise<{ header?: string; footer?: string; chunks: string[] }> => {
    const stepRegex = /\*\*Step \d+:.*?(?=\*\*Step \d+:|$)/gs;
    const chunks = text.match(stepRegex) || [];
    return Promise.resolve({ chunks });
  },
  // TODO: eventually deprecate the above and use this one, but need them right now for backwards compatibility
  ChunkByRegex: async (
    text: string,
    params: z.infer<(typeof ProcessChunkingStrategyParamsMap)['ChunkByRegex']>,
  ): Promise<{ header?: string; footer?: string; chunks: string[] }> => {
    const regex = new RegExp(params.regex, 'g');
    const chunks = params.inclusive
      ? text.match(new RegExp(`(${params.regex}.*?)(?=${params.regex}|$)`, 'gs')) || []
      : text.split(regex);
    return Promise.resolve({ chunks });
  },
  ChunkWithGPT: async (
    text: string,
    params: z.infer<(typeof ProcessChunkingStrategyParamsMap)['ChunkWithGPT']>,
  ): Promise<{ header?: string; footer?: string; chunks: string[] }> => {
    const systemPrompt = params.systemPrompt
      ? params.systemPrompt
      : 'Please split the following message into logical chunks, which should each be separated by a /chunk/ character (literally just use the exact text: /chunk/). These should be separated logical chunks so that a user can rate each one separately based on factuality, so each chunk contains only one major fact and its subfacts. Do not add ANY extra words or annotations other than the /chunk/ character that separates each chunk (e.g. do not say "Chunk 1" or "Chunk 2"), and do not include quotations. The content should match exactly aside from the chunk characters that separate each chunk. Here is the message:\n\n';
    const body = {
      strategyName: params.modelStrategyName || 'OpenAIStrategy',
      params: {
        model: params.model || 'gpt-4-turbo-preview',
        temperature: params.temperature || 0.2,
        ...params.model_parameters,
      },
      messages: [
        {
          content: systemPrompt + text,
          role: 'user',
        },
      ],
      useCase: 'process-supervision chunking',
    };

    const response = await apiFetch(null, 'internal/self_serve/rlhf/createCompletion', {
      method: 'POST',
      body,
    });

    let chunks = response?.choices?.[0]?.content?.split('/chunk/') || [];

    if (!chunks.length) {
      return Promise.resolve({ chunks: [] });
    }

    chunks = chunks.map((chunk: string) => chunk.trim());
    chunks = chunks.filter((chunk: string) => chunk?.length);

    if (!params.skipValidation) {
      const validation = validateStepContentMatches(
        chunks.map((chunk: string) => ({ content: chunk, rating: 0 })),
        text,
        // ignore the chunk character /chunk/:
        /\s*\/chunk\/\s*/g,
        params.maxLevenshteinDistance || 0,
      );

      if (!validation.success) {
        return Promise.resolve({ chunks: [] });
      }
    }

    return Promise.resolve({ chunks });
  },
  ChunkByDelimiterPair: async (text: string, _params) => {
    const { startDelimiter, endDelimiter } = _params;

    const chunks: string[] = [];
    let currentIndex = 0;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const startIndex = text.indexOf(startDelimiter, currentIndex);
      const endIndex = text.indexOf(endDelimiter, startIndex + startDelimiter.length);

      if (startIndex === -1 || endIndex === -1) {
        // TODO: could indicate malformed text; how should we handle that case?
        break;
      }

      chunks.push(text.slice(startIndex + startDelimiter.length, endIndex));
      currentIndex = endIndex + endDelimiter.length;
    }

    return Promise.resolve({ chunks });
  },
};

// NOTE: this is for Vaskar's experimental tool use component
export const SubProcessChunkingStrategyMap: {
  [key: string]: (
    chunk: string[],
    fieldIds: string[],
    chunkMask: number[],
    params?: any,
  ) => Promise<{
    header?: string;
    footer?: string;
    subChunks: string[];
    subStepEndIndices: number[];
    subStepSkipCorrectButtonIndices: number[];
  }>;
} = {
  ChunkByUniqueKeyList: async (
    chunks: string[],
    fieldIds: string[],
    chunkMask: number[] = [],
  ): Promise<{
    header?: string;
    footer?: string;
    subChunks: string[];
    subStepEndIndices: number[];
    subStepSkipCorrectButtonIndices: number[];
  }> => {
    const subStepEndIndices: number[] = [];
    const subStepSkipCorrectButtonIndices: number[] = [];
    let currSubChunkIndex = 0;

    const subChunks = _.flatten(
      chunks.map(text => {
        return fieldIds.map((fieldId, index) => {
          if (!chunkMask[index]) {
            subStepSkipCorrectButtonIndices.push(currSubChunkIndex);
          }

          if (index === fieldIds.length - 1) {
            // want regex similar to this `/\*\*${fieldId}:.*?/gs`;
            const startIndex = text.indexOf(`**${fieldId}**:`);
            const offset = `**${fieldId}**: `.length;
            subStepEndIndices.push(currSubChunkIndex);
            currSubChunkIndex += 1;
            return text.slice(startIndex + offset).trim();
          } else {
            const startIndex = text.indexOf(`**${fieldId}**:`);
            const offset = `**${fieldId}**: `.length;
            const endIndex = text.indexOf(`**${fieldIds[index + 1]}**:`);
            currSubChunkIndex += 1;
            return text.slice(startIndex + offset, endIndex).trim();
          }
        });
      }),
    );

    return Promise.resolve({ subChunks, subStepEndIndices, subStepSkipCorrectButtonIndices });
  },
};
